import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  RefreshCw, DollarSign, Clock, User, Package, Loader2, 
  AlertCircle, CheckCircle, Calendar, TrendingDown, Search,
  Zap, Timer, Activity
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RefundLogsDashboard() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refundLogs, setRefundLogs] = useState([]);
  const [stats, setStats] = useState({
    count: 0,
    total_refunded: 0,
    auto_refunds: 0,
    manual_refunds: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [schedulerStatus, setSchedulerStatus] = useState({ is_running: false });
  const [runningRefundCheck, setRunningRefundCheck] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const [logsRes, statusRes] = await Promise.all([
        axios.get(`${API}/scheduler/refund-logs?limit=100`, { headers, withCredentials: true }),
        axios.get(`${API}/scheduler/status`, { headers, withCredentials: true })
      ]);
      
      setRefundLogs(logsRes.data.logs || []);
      setStats({
        count: logsRes.data.count || 0,
        total_refunded: logsRes.data.total_refunded || 0,
        auto_refunds: logsRes.data.auto_refunds || 0,
        manual_refunds: logsRes.data.manual_refunds || 0
      });
      setSchedulerStatus(statusRes.data || { is_running: false });
    } catch (error) {
      console.error('Failed to fetch refund data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every minute
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRunRefundCheck = async () => {
    setRunningRefundCheck(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await axios.post(
        `${API}/scheduler/run-refund-check`,
        {},
        { headers, withCredentials: true }
      );
      
      toast.success(`Refund check complete! ${response.data.refunds_processed} orders refunded.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run refund check');
    } finally {
      setRunningRefundCheck(false);
    }
  };

  const filteredLogs = refundLogs.filter(log =>
    log.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.order_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.reason?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderTypeBadge = (type) => {
    switch (type) {
      case 'food_order':
        return <Badge className="bg-orange-100 text-orange-700">Food Order</Badge>;
      case 'auto_parts_order':
        return <Badge className="bg-blue-100 text-blue-700">Auto Parts</Badge>;
      case 'service_request':
        return <Badge className="bg-green-100 text-green-700">Service</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-100 rounded-xl">
                <DollarSign className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Refunded</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.total_refunded)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-yellow-100 rounded-xl">
                <Zap className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Auto Refunds</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.auto_refunds}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-xl">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Manual Refunds</p>
                <p className="text-2xl font-bold text-blue-600">{stats.manual_refunds}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="card-glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-xl">
                <Activity className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Scheduler</p>
                <p className="text-lg font-bold text-green-600 flex items-center gap-2">
                  {schedulerStatus.is_running ? (
                    <>
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      Running
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 bg-gray-400 rounded-full" />
                      Stopped
                    </>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-blue-800">Auto-Refund Policy</p>
              <p className="text-sm text-blue-700 mt-1">
                Orders not completed within <strong>24 hours</strong> are automatically refunded in full.
                Auto parts orders with shipping get <strong>48 hours</strong> for delivery.
                The scheduler runs hourly to check for eligible refunds.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions & Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search by customer, order ID, reason..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button 
            onClick={handleRunRefundCheck}
            disabled={runningRefundCheck}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 gap-2"
          >
            {runningRefundCheck ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <Timer className="h-4 w-4" />
                Run Refund Check Now
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Refund Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-red-500" />
            Refund History
          </CardTitle>
          <CardDescription>
            Detailed log of all refunds processed (auto and manual)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-300" />
              <p className="text-lg font-medium">No Refunds Yet</p>
              <p className="text-sm text-gray-400 mt-1">All orders are being completed on time!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredLogs.map((log) => (
                <div 
                  key={log.refund_id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-4"
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${log.refund_type === 'auto' ? 'bg-yellow-100' : 'bg-blue-100'}`}>
                      {log.refund_type === 'auto' ? (
                        <Zap className="h-5 w-5 text-yellow-600" />
                      ) : (
                        <User className="h-5 w-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{log.customer_name}</span>
                        {getOrderTypeBadge(log.order_type)}
                        <Badge variant={log.refund_type === 'auto' ? 'default' : 'outline'}>
                          {log.refund_type === 'auto' ? 'Auto' : 'Manual'}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 font-mono">#{log.order_id?.slice(-12)}</p>
                      <p className="text-sm text-gray-600 mt-1">{log.reason}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6 sm:ml-auto">
                    <div className="text-right">
                      <p className="text-lg font-bold text-red-600">{formatCurrency(log.amount)}</p>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        {formatDate(log.processed_at)}
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {log.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scheduler Logs */}
      {schedulerStatus.recent_logs?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Scheduler Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {schedulerStatus.recent_logs.slice(0, 10).map((log, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
                  <div className="flex items-center gap-3">
                    <Badge variant={log.task === 'daily_scraper' ? 'default' : 'secondary'}>
                      {log.task?.replace('_', ' ')}
                    </Badge>
                    <span className="text-gray-500">{formatDate(log.timestamp)}</span>
                  </div>
                  <div className="text-gray-600">
                    {log.task === 'auto_refund' && (
                      <span>{log.refunds_processed || 0} refunds</span>
                    )}
                    {log.task === 'daily_scraper' && (
                      <span>
                        {log.results?.restaurants_updated || 0} restaurants, 
                        {log.results?.auto_parts_ads_updated || 0} parts
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
